import React, {
    useCallback,
    useContext,
    useRef,
    useState,
} from "react";

import {
    Button,
    Container,
    Icon,
    Input,
    Label,
    Loader,
    Message,
    Segment,
} from "semantic-ui-react";

import {
    collection,
    getDocs,
    query,
    where,
} from "firebase/firestore";

import { db } from "../firebase/firebaseConfig";
import { UserContext } from "../context/UserContext";

const ConsultaEquipos = () => {

    const { users } = useContext(UserContext);

    const inputRef = useRef(null);

    const [serie, setSerie] = useState("");
    const [buscando, setBuscando] = useState(false);
    const [error, setError] = useState("");

    const [equipo, setEquipo] = useState(null);
    const [mantencion, setMantencion] = useState(null);
    const [ultimoServicio, setUltimoServicio] = useState(null);

    // =========================================================
    // HELPERS
    // =========================================================

    const normalizarSerie = (valor) => {
        return String(valor || "")
            .trim()
            .toUpperCase();
    };

    const fechaAJS = (valor) => {

        if (!valor) return null;

        if (typeof valor?.toDate === "function") {
            return valor.toDate();
        }

        if (valor instanceof Date) {
            return valor;
        }

        const fecha = new Date(valor);

        if (Number.isNaN(fecha.getTime())) {
            return null;
        }

        return fecha;
    };

    const formatearFecha = (valor) => {

        const fecha = fechaAJS(valor);

        if (!fecha) return "—";

        return fecha.toLocaleDateString("es-CL");
    };

    const obtenerFechaOrden = (item) => {

        return (
            fechaAJS(item.fecha_termino) ||
            fechaAJS(item.fecha_out) ||
            fechaAJS(item.fecha_cierre) ||
            fechaAJS(item.fechamod) ||
            fechaAJS(item.fechaadd) ||
            fechaAJS(item.date) ||
            null
        );
    };

    const obtenerEstadoMantencion = (mant) => {

        if (!mant) {
            return {
                texto: "SIN PROGRAMA",
                color: "grey",
                descripcion: "No existe una mantención programada",
            };
        }

        const termino = fechaAJS(mant.fecha_termino);

        if (!termino) {
            return {
                texto: mant.estado || "PENDIENTE",
                color: "grey",
                descripcion: "Sin fecha de próxima mantención",
            };
        }

        const hoy = new Date();

        hoy.setHours(0, 0, 0, 0);

        const fechaTermino = new Date(termino);

        fechaTermino.setHours(0, 0, 0, 0);

        const diferenciaMs =
            fechaTermino.getTime() - hoy.getTime();

        const dias = Math.ceil(
            diferenciaMs / (1000 * 60 * 60 * 24)
        );

        if (dias < 0) {
            return {
                texto: "VENCIDA",
                color: "red",
                descripcion:
                    `Vencida hace ${Math.abs(dias)} día${
                        Math.abs(dias) === 1 ? "" : "s"
                    }`,
            };
        }

        if (dias === 0) {
            return {
                texto: "VENCE HOY",
                color: "red",
                descripcion: "La mantención vence hoy",
            };
        }

        if (dias <= 7) {
            return {
                texto: "POR VENCER",
                color: "orange",
                descripcion:
                    `Faltan ${dias} día${
                        dias === 1 ? "" : "s"
                    }`,
            };
        }

        if (dias <= 30) {
            return {
                texto: "PRÓXIMA",
                color: "yellow",
                descripcion:
                    `Faltan ${dias} días`,
            };
        }

        return {
            texto: "AL DÍA",
            color: "green",
            descripcion:
                `Próxima en ${dias} días`,
        };
    };

    const colorEstadoEquipo = (estado) => {

        const valor = String(estado || "")
            .trim()
            .toUpperCase();

        switch (valor) {

            case "PREPARACION":
            case "PREPARACIÓN":
                return "yellow";

            case "BODEGA":
                return "blue";

            case "PACIENTE":
                return "green";

            case "SERVICIO TECNICO":
            case "SERVICIO TÉCNICO":
                return "violet";

            case "PROVEEDOR":
                return "purple";

            case "TRANSITO PACIENTE":
            case "TRÁNSITO PACIENTE":
                return "teal";

            case "TRANSITO ST":
            case "TRÁNSITO ST":
                return "orange";

            case "TRANSITO BODEGA":
            case "TRÁNSITO BODEGA":
                return "teal";

            case "TRANSITO PROVEEDOR":
            case "TRÁNSITO PROVEEDOR":
                return "red";

            case "BAJA":
                return "grey";

            default:
                return "grey";
        }
    };

    // =========================================================
    // CONSULTAR MANTENCION
    // =========================================================

    const consultarMantencion = async (
        equipoStatus,
        statusId
    ) => {

        try {

            /*
             * Regla:
             *
             * mantenciones.id_eq debe contener el ID real
             * del equipo.
             *
             * Intentamos primero campos explícitos y finalmente
             * el ID del documento de status.
             */

            const idEquipo =
                equipoStatus.eq_id ||
                equipoStatus.id_eq ||
                equipoStatus.equipo_id ||
                statusId;

            if (!idEquipo) {
                return null;
            }

            const qMant = query(
                collection(db, "mantenciones"),
                where("emp_id", "==", users.emp_id),
                where("id_eq", "==", idEquipo)
            );

            const snap = await getDocs(qMant);

            if (snap.empty) {
                return null;
            }

            const registros = snap.docs.map((d) => ({
                id: d.id,
                ...d.data(),
            }));

            /*
             * Preferimos la mantención activa.
             * Si hay más de una, elegimos la que tenga
             * fecha_termino más reciente.
             */

            registros.sort((a, b) => {

                const fa =
                    fechaAJS(a.fecha_termino)?.getTime() || 0;

                const fb =
                    fechaAJS(b.fecha_termino)?.getTime() || 0;

                return fb - fa;
            });

            return (
                registros.find(
                    (m) => m.activo !== false
                ) ||
                registros[0] ||
                null
            );

        } catch (error) {

            console.error(
                "Error consultando mantención:",
                error
            );

            return null;
        }
    };

    // =========================================================
    // CONSULTAR ÚLTIMO SERVICIO TÉCNICO
    // =========================================================

    const consultarUltimoServicio = async (
        serieEquipo
    ) => {

        try {

            const resultados = [];

            /*
             * 1. Órdenes ST pertenecientes al tenant actual.
             *
             * Esto sirve cuando Consulta Equipos se utiliza
             * directamente dentro de LogisticMed ST.
             */

            try {

                const qActual = query(
                    collection(db, "ingresostcab"),
                    where("emp_id", "==", users.emp_id),
                    where("serie", "==", serieEquipo)
                );

                const snapActual =
                    await getDocs(qActual);

                snapActual.forEach((d) => {

                    resultados.push({
                        id: d.id,
                        ...d.data(),
                    });

                });

            } catch (error) {

                console.warn(
                    "No fue posible consultar órdenes ST del tenant actual:",
                    error
                );
            }

            /*
             * 2. Órdenes creadas por HomeCare hacia ST interno.
             *
             * Los ingresos ST generados desde un HomeCare
             * conservan emp_id_origen.
             */

            try {

                const qOrigen = query(
                    collection(db, "ingresostcab"),
                    where(
                        "emp_id_origen",
                        "==",
                        users.emp_id
                    ),
                    where(
                        "serie",
                        "==",
                        serieEquipo
                    )
                );

                const snapOrigen =
                    await getDocs(qOrigen);

                snapOrigen.forEach((d) => {

                    if (
                        !resultados.some(
                            (r) => r.id === d.id
                        )
                    ) {
                        resultados.push({
                            id: d.id,
                            ...d.data(),
                        });
                    }

                });

            } catch (error) {

                console.warn(
                    "No fue posible consultar órdenes ST provenientes de HomeCare:",
                    error
                );
            }

            if (resultados.length === 0) {
                return null;
            }

            resultados.sort((a, b) => {

                const fa =
                    obtenerFechaOrden(a)?.getTime() || 0;

                const fb =
                    obtenerFechaOrden(b)?.getTime() || 0;

                return fb - fa;
            });

            return resultados[0];

        } catch (error) {

            console.error(
                "Error consultando último servicio:",
                error
            );

            return null;
        }
    };

    // =========================================================
    // BUSCAR EQUIPO
    // =========================================================

    const buscarEquipo = useCallback(
        async (serieRecibida = null) => {

            const serieBuscar = normalizarSerie(
                serieRecibida ?? serie
            );

            if (!serieBuscar) {

                setError(
                    "Ingrese o escanee un número de serie."
                );

                return;
            }

            if (!users?.emp_id) {

                setError(
                    "No se pudo determinar la empresa del usuario."
                );

                return;
            }

            setBuscando(true);
            setError("");

            setEquipo(null);
            setMantencion(null);
            setUltimoServicio(null);

            try {

                // =========================================
                // STATUS
                // =========================================

                const qStatus = query(
                    collection(db, "status"),
                    where(
                        "emp_id",
                        "==",
                        users.emp_id
                    ),
                    where(
                        "serie",
                        "==",
                        serieBuscar
                    )
                );

                const snapStatus =
                    await getDocs(qStatus);

                if (snapStatus.empty) {

                    setError(
                        `No se encontró un equipo con la serie ${serieBuscar}.`
                    );

                    return;
                }

                const docStatus =
                    snapStatus.docs[0];

                const equipoEncontrado = {
                    id: docStatus.id,
                    ...docStatus.data(),
                };

                setEquipo(equipoEncontrado);

                // =========================================
                // CONSULTAS COMPLEMENTARIAS
                // =========================================

                const [
                    mantEncontrada,
                    servicioEncontrado,
                ] = await Promise.all([
                    consultarMantencion(
                        equipoEncontrado,
                        docStatus.id
                    ),

                    consultarUltimoServicio(
                        serieBuscar
                    ),
                ]);

                setMantencion(mantEncontrada);
                setUltimoServicio(
                    servicioEncontrado
                );

            } catch (error) {

                console.error(
                    "Error buscando equipo:",
                    error
                );

                setError(
                    error?.message ||
                    "Ocurrió un error al consultar el equipo."
                );

            } finally {

                setBuscando(false);
            }

        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [serie, users?.emp_id]
    );

    // =========================================================
    // ENTER / LECTOR DE CÓDIGO DE BARRAS
    // =========================================================

    const handleKeyDown = (e) => {

        if (e.key === "Enter") {

            e.preventDefault();

            buscarEquipo();
        }
    };

    // =========================================================
    // LIMPIAR
    // =========================================================

    const limpiar = () => {

        setSerie("");
        setEquipo(null);
        setMantencion(null);
        setUltimoServicio(null);
        setError("");

        setTimeout(() => {

            inputRef.current?.focus();

        }, 50);
    };

    // =========================================================
    // DATOS CALCULADOS
    // =========================================================

    const estadoMantencion =
        obtenerEstadoMantencion(mantencion);

    const estadoActual =
        equipo?.n_destino ||
        equipo?.status ||
        equipo?.estado ||
        "SIN ESTADO";

    const ubicacionActual =
        equipo?.n_permanente ||
        equipo?.entidad ||
        equipo?.n_destino ||
        "—";

    // =========================================================
    // RENDER
    // =========================================================

    return (

        <div
            style={{
                minHeight: "100vh",
                background: "#F4F6F8",
                padding: "14px 0 28px",
            }}
        >

            <Container
                style={{
                    width: "100%",
                    maxWidth: 520,
                    paddingLeft: 12,
                    paddingRight: 12,
                }}
            >

                {/* =========================================
                    TITULO
                ========================================= */}

                <div
                    style={{
                        marginBottom: 14,
                    }}
                >
                    <div
                        style={{
                            fontSize: 21,
                            fontWeight: 700,
                            color: "#172b4d",
                        }}
                    >
                        Consulta Equipo
                    </div>

                    <div
                        style={{
                            fontSize: 13,
                            color: "#6b778c",
                            marginTop: 3,
                        }}
                    >
                        Busque por número de serie
                    </div>
                </div>

                {/* =========================================
                    BUSCADOR
                ========================================= */}

                <Segment
                    style={{
                        border: "1px solid #e5e9ef",
                        borderRadius: 10,
                        boxShadow:
                            "0 1px 3px rgba(9,30,66,0.08)",
                        padding: 14,
                        marginBottom: 14,
                    }}
                >

                    <div
                        style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#5e6c84",
                            marginBottom: 6,
                        }}
                    >
                        NÚMERO DE SERIE
                    </div>

                    <Input
                        ref={inputRef}
                        fluid
                        autoFocus
                        value={serie}
                        placeholder="Escanee o ingrese la serie"
                        disabled={buscando}
                        icon="barcode"
                        iconPosition="left"
                        onChange={(e) =>
                            setSerie(
                                e.target.value.toUpperCase()
                            )
                        }
                        onKeyDown={handleKeyDown}
                        style={{
                            marginBottom: 10,
                        }}
                    />

                    <div
                        style={{
                            display: "flex",
                            gap: 8,
                        }}
                    >

                        <Button
                            fluid
                            primary
                            type="button"
                            loading={buscando}
                            disabled={
                                buscando ||
                                !serie.trim()
                            }
                            onClick={() =>
                                buscarEquipo()
                            }
                            style={{
                                background: "#0B4DA2",
                            }}
                        >
                            <Icon name="search" />
                            Buscar
                        </Button>

                        {(serie || equipo) && (

                            <Button
                                basic
                                icon
                                type="button"
                                title="Limpiar"
                                disabled={buscando}
                                onClick={limpiar}
                            >
                                <Icon name="close" />
                            </Button>
                        )}

                    </div>

                    <div
                        style={{
                            marginTop: 10,
                            fontSize: 11,
                            color: "#8993a4",
                            lineHeight: 1.4,
                        }}
                    >
                        También puede usar un lector de
                        código de barras conectado al celular
                        o computador.
                    </div>

                </Segment>

                {/* =========================================
                    ERROR
                ========================================= */}

                {error && (

                    <Message
                        negative
                        size="small"
                        style={{
                            borderRadius: 8,
                        }}
                    >
                        <Icon name="exclamation circle" />
                        {error}
                    </Message>
                )}

                {/* =========================================
                    LOADER
                ========================================= */}

                {buscando && (

                    <Segment
                        style={{
                            minHeight: 130,
                            borderRadius: 10,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Loader active inline="centered">
                            Consultando equipo...
                        </Loader>
                    </Segment>
                )}

                {/* =========================================
                    RESULTADO
                ========================================= */}

                {!buscando && equipo && (

                    <>
                        {/* =================================
                            EQUIPO
                        ================================= */}

                        <Segment
                            style={{
                                borderRadius: 10,
                                border:
                                    "1px solid #e5e9ef",
                                padding: 16,
                                marginBottom: 10,
                                boxShadow:
                                    "0 1px 3px rgba(9,30,66,0.08)",
                            }}
                        >

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent:
                                        "space-between",
                                    alignItems:
                                        "flex-start",
                                    gap: 10,
                                }}
                            >

                                <div>

                                    <div
                                        style={{
                                            fontSize: 11,
                                            color: "#8993a4",
                                            textTransform:
                                                "uppercase",
                                            fontWeight: 700,
                                        }}
                                    >
                                        Serie
                                    </div>

                                    <div
                                        style={{
                                            fontSize: 20,
                                            fontWeight: 700,
                                            color: "#172b4d",
                                            marginTop: 2,
                                        }}
                                    >
                                        {equipo.serie}
                                    </div>

                                </div>

                                <Label
                                    color={colorEstadoEquipo(
                                        estadoActual
                                    )}
                                    style={{
                                        margin: 0,
                                        whiteSpace:
                                            "normal",
                                        textAlign:
                                            "center",
                                    }}
                                >
                                    {estadoActual}
                                </Label>

                            </div>

                            <div
                                style={{
                                    marginTop: 13,
                                    paddingTop: 12,
                                    borderTop:
                                        "1px solid #edf0f4",
                                }}
                            >

                                <div
                                    style={{
                                        fontSize: 16,
                                        fontWeight: 700,
                                        color: "#253858",
                                    }}
                                >
                                    {equipo.marca || "—"}{" "}
                                    {equipo.modelo || ""}
                                </div>

                                <div
                                    style={{
                                        fontSize: 13,
                                        color: "#6b778c",
                                        marginTop: 3,
                                    }}
                                >
                                    {equipo.tipo ||
                                        equipo.familia ||
                                        "Equipo"}
                                </div>

                            </div>

                        </Segment>

                        {/* =================================
                            UBICACION
                        ================================= */}

                        <Segment
                            style={{
                                borderRadius: 10,
                                border:
                                    "1px solid #e5e9ef",
                                padding: 14,
                                marginBottom: 10,
                                boxShadow:
                                    "0 1px 3px rgba(9,30,66,0.06)",
                            }}
                        >

                            <div
                                style={{
                                    display: "flex",
                                    gap: 11,
                                    alignItems: "center",
                                }}
                            >

                                <div
                                    style={{
                                        width: 36,
                                        height: 36,
                                        minWidth: 36,
                                        borderRadius: 8,
                                        background: "#eaf2ff",
                                        display: "flex",
                                        alignItems:
                                            "center",
                                        justifyContent:
                                            "center",
                                    }}
                                >
                                    <Icon
                                        name="map marker alternate"
                                        style={{
                                            margin: 0,
                                            color: "#0B4DA2",
                                        }}
                                    />
                                </div>

                                <div>

                                    <div
                                        style={{
                                            fontSize: 11,
                                            color: "#8993a4",
                                            fontWeight: 700,
                                        }}
                                    >
                                        UBICACIÓN
                                    </div>

                                    <div
                                        style={{
                                            fontSize: 15,
                                            fontWeight: 700,
                                            color: "#253858",
                                            marginTop: 2,
                                        }}
                                    >
                                        {ubicacionActual}
                                    </div>

                                    {equipo.r_permanente && (

                                        <div
                                            style={{
                                                fontSize: 12,
                                                color: "#6b778c",
                                                marginTop: 2,
                                            }}
                                        >
                                            RUT:{" "}
                                            {
                                                equipo.r_permanente
                                            }
                                        </div>
                                    )}

                                </div>

                            </div>

                        </Segment>

                        {/* =================================
                            MANTENCION
                        ================================= */}

                        <Segment
                            style={{
                                borderRadius: 10,
                                border:
                                    "1px solid #e5e9ef",
                                padding: 14,
                                marginBottom: 10,
                                boxShadow:
                                    "0 1px 3px rgba(9,30,66,0.06)",
                            }}
                        >

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent:
                                        "space-between",
                                    alignItems: "center",
                                    gap: 8,
                                    marginBottom: 12,
                                }}
                            >

                                <div
                                    style={{
                                        display: "flex",
                                        gap: 8,
                                        alignItems: "center",
                                    }}
                                >
                                    <Icon
                                        name="wrench"
                                        style={{
                                            color: "#00a8b5",
                                        }}
                                    />

                                    <strong
                                        style={{
                                            color: "#253858",
                                        }}
                                    >
                                        Mantención
                                    </strong>
                                </div>

                                <Label
                                    color={
                                        estadoMantencion.color
                                    }
                                    size="tiny"
                                >
                                    {estadoMantencion.texto}
                                </Label>

                            </div>

                            {mantencion ? (

                                <>
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns:
                                                "1fr 1fr",
                                            gap: 12,
                                        }}
                                    >

                                        <div>

                                            <div
                                                style={{
                                                    fontSize: 11,
                                                    color: "#8993a4",
                                                }}
                                            >
                                                Última
                                            </div>

                                            <div
                                                style={{
                                                    fontSize: 14,
                                                    fontWeight: 700,
                                                    color: "#253858",
                                                    marginTop: 2,
                                                }}
                                            >
                                                {formatearFecha(
                                                    mantencion.fecha_inicio
                                                )}
                                            </div>

                                        </div>

                                        <div>

                                            <div
                                                style={{
                                                    fontSize: 11,
                                                    color: "#8993a4",
                                                }}
                                            >
                                                Próxima
                                            </div>

                                            <div
                                                style={{
                                                    fontSize: 14,
                                                    fontWeight: 700,
                                                    color: "#253858",
                                                    marginTop: 2,
                                                }}
                                            >
                                                {formatearFecha(
                                                    mantencion.fecha_termino
                                                )}
                                            </div>

                                        </div>

                                    </div>

                                    <div
                                        style={{
                                            marginTop: 10,
                                            fontSize: 12,
                                            color: "#6b778c",
                                        }}
                                    >
                                        {
                                            estadoMantencion.descripcion
                                        }

                                        {mantencion.programa && (
                                            <>
                                                {" · "}
                                                {
                                                    mantencion.programa
                                                }
                                            </>
                                        )}
                                    </div>
                                </>

                            ) : (

                                <div
                                    style={{
                                        fontSize: 13,
                                        color: "#6b778c",
                                    }}
                                >
                                    No existe una mantención
                                    programada para este equipo.
                                </div>
                            )}

                        </Segment>

                        {/* =================================
                            ULTIMO SERVICIO ST
                        ================================= */}

                        <Segment
                            style={{
                                borderRadius: 10,
                                border:
                                    "1px solid #e5e9ef",
                                padding: 14,
                                marginBottom: 10,
                                boxShadow:
                                    "0 1px 3px rgba(9,30,66,0.06)",
                            }}
                        >

                            <div
                                style={{
                                    display: "flex",
                                    gap: 8,
                                    alignItems: "center",
                                    marginBottom:
                                        ultimoServicio
                                            ? 11
                                            : 0,
                                }}
                            >
                                <Icon
                                    name="settings"
                                    style={{
                                        color: "#6435c9",
                                    }}
                                />

                                <strong
                                    style={{
                                        color: "#253858",
                                    }}
                                >
                                    Último servicio ST
                                </strong>
                            </div>

                            {ultimoServicio ? (

                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent:
                                            "space-between",
                                        alignItems:
                                            "flex-start",
                                        gap: 12,
                                    }}
                                >

                                    <div>

                                        <div
                                            style={{
                                                fontSize: 14,
                                                fontWeight: 700,
                                                color: "#253858",
                                            }}
                                        >
                                            {ultimoServicio.servicio ||
                                                "Servicio técnico"}
                                        </div>

                                        <div
                                            style={{
                                                fontSize: 12,
                                                color: "#6b778c",
                                                marginTop: 3,
                                            }}
                                        >
                                            Orden{" "}
                                            {ultimoServicio.folio ||
                                                ultimoServicio.numdoc ||
                                                "—"}
                                            {" · "}
                                            {formatearFecha(
                                                obtenerFechaOrden(
                                                    ultimoServicio
                                                )
                                            )}
                                        </div>

                                    </div>

                                    {ultimoServicio.estado && (

                                        <Label
                                            basic
                                            color="violet"
                                            size="tiny"
                                            style={{
                                                margin: 0,
                                            }}
                                        >
                                            {
                                                ultimoServicio.estado
                                            }
                                        </Label>
                                    )}

                                </div>

                            ) : (

                                <div
                                    style={{
                                        fontSize: 13,
                                        color: "#6b778c",
                                    }}
                                >
                                    No se encontraron servicios
                                    técnicos registrados.
                                </div>
                            )}

                        </Segment>
                    </>
                )}

            </Container>

        </div>
    );
};

export default ConsultaEquipos;